import React from 'react';
import { AffiliationStatus, ApplicationStatus, TeamType } from '../constants/constants';
import { getLandingByPageKey } from '../constants/TopTeams';

const US_COUNTRY_CODE = 'US';
export const QUALIFY_DESTINATION = '/user/qualify';

export const noop = () => {};

export const InstanceContext = React.createContext(null);

export const formatAddress = (address) => {
  if (!address) return '';

  const { city, state, countryCode } = address;

  const result = (city && state ? `${city}, ${state}` : `${city || ''}${state || ''}`).trim();
  return countryCode && countryCode !== US_COUNTRY_CODE ? `${result}, ${countryCode}` : result;
};

export const formatLocationCount = (count = 0) => {
  if (!count) return '';
  return count > 10 ? '10+' : '1-10';
};

export const formatMemberCount = (count = 0) => {
  if (count > 250) return '250+';
  if (count > 50) return '50-249';
  if (count > 10) return '10-49';
  return '1-49';
};

export const isNative = () =>
  /Experticity|Android|webOS|iPhone|iPad|iPod|BlackBerry|BB|PlayBook|IEMobile|Windows Phone|Kindle|Silk|Opera Mini/i.test(
    navigator.userAgent,
  );

export const sanitizeObject = (value) => {
  if (typeof value !== 'object' || value === null || value === undefined) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(sanitizeObject);
  }

  return Object.entries(value)
    .filter(([, v]) => typeof v !== 'function' && typeof v !== 'symbol' && v !== null && v !== undefined)
    .reduce((map, [k, v]) => {
      if (Array.isArray(v)) {
        return { ...map, [k]: v.map(sanitizeObject) };
      }
      if (typeof v === 'object') {
        return { ...map, [k]: sanitizeObject(v) };
      }
      return { ...map, [k]: v };
    }, {});
};

// Trims a string to a specified max number of characters
export const trimLongStrings = (str, max) => (str?.length >= max ? str.substring(0, max) : str);

// Creates a group page url from a teamDto
export const getCommunityPageUrl = ({ type, teamKey, orgKey }) =>
  (type === 'PROMOTIVE' && teamKey && `/group/${teamKey}`) ||
  (type === 'RETAIL' && orgKey && `/retailer/${orgKey}`) ||
  '';

const urlPrefix = '/user/application';
// Standalone application page
export const getApplicationUrl = (teamId, teamKey) => `${urlPrefix}/apply/${teamId}/${teamKey || 'team'}`;

export const getApplicationStatusUrl = (teamId) => `${urlPrefix}/team/${teamId}/latest`;

export const getApplicationApprovedUrl = (teamId) => `${urlPrefix}/approved?teamId=${teamId}`;

export const isDateValid = ({ year, month, day }) => {
  const dateFromModel = new Date(year, month - 1, day);
  if (
    dateFromModel?.getFullYear?.() !== parseInt(year, 10) ||
    dateFromModel?.getMonth?.() !== month - 1 ||
    dateFromModel?.getDate?.() !== parseInt(day, 10)
  ) {
    return false;
  }
  return true;
};

// Name of the cookie an invite link writes before redirecting into onboarding. Set outside this
// project (the invite-link handler), so the widget only ever reads it.
const REFERRAL_COOKIE = 'experticityReferral';

/**
 * Reads the `experticityReferral` cookie an invite URL leaves behind. The redirect into onboarding
 * only carries `onb_mode=referral&onb_suggestedGroups=<teamId>` in the query string, so the cookie
 * is the only place the inviter's uuid is available to the widget.
 *
 * Returns an empty object when the cookie is missing or unparseable — callers treat every field as
 * optional, and a malformed cookie must never break sign-up.
 */
export const getReferralCookie = () => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${REFERRAL_COOKIE}=([^;]*)`));
  if (!match) return {};

  try {
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const getOnbCacheKey = (uuid) => `ev_user_onb_${uuid}`;

/**
 * Resolves the team classes of the landing page the user arrived on.
 *
 * Hosts pass `landingTeamClassIds` / `initialLandingPageKey` on the config of the session the user
 * signs up in; InitialPage caches them so the later `setup()` and `qualify()` sessions — which get a
 * fresh config without them — can still attribute their events. Config wins so a host that passes
 * them explicitly is always honored.
 */
export const getLandingTeamClassIds = (config = {}, cached = {}) =>
  config.landingTeamClassIds?.length ? config.landingTeamClassIds : cached.landingTeamClassIds;

/** True when the sign-up cache carries landing attribution of its own. */
export const hasCachedLanding = (cached) =>
  Boolean(cached?.landingTeamClassIds?.length || cached?.initialLandingPageKey);

/**
 * Resolves `?onb_landing=<pageKey>`, the key an already-signed-in visitor is redirected in from
 * when they open a landing page — they never pass through sign-up, so nothing cached the landing
 * identifiers for them.
 *
 * Deliberately transient: it is never written to the cache, so a later visit without the param
 * behaves like any other. And it yields only to what is already there — a visitor who has landing
 * identifiers on the config or in the cache arrived the normal way and refreshed the redirect URL,
 * so that attribution is the real one.
 */
const transientLanding = (config = {}, cached = {}) => {
  const established = config.landingTeamClassIds?.length || config.initialLandingPageKey || hasCachedLanding(cached);

  return established ? undefined : getLandingByPageKey(config.landing);
};

/**
 * Team classes that curate the browse list — the Top Teams list and the headline that replaces the
 * tabs. Wider than `getLandingTeamClassIds` by the `?onb_landing=` fallback, which curates browsing
 * for that one visit but deliberately does not narrow search the way a cached landing does.
 */
export const getBrowseTeamClassIds = (config = {}, cached = {}) =>
  getLandingTeamClassIds(config, cached) || transientLanding(config, cached)?.landingTeamClassIds;

/**
 * The landing-page identifiers that ride along on every analytic event.
 *
 * The team classes go out comma-joined because the analytics collector drops array values
 * outright — `[19, 29]` would never reach a report, `'19,29'` does.
 *
 * Both are optional: an absent, empty, or empty-array value is omitted entirely rather than sent as
 * `undefined`/`[]`, so events that have no landing context look exactly as they do today.
 */
export const getLandingAnalytics = (config = {}, cached = {}) => {
  const transient = transientLanding(config, cached);
  const landingTeamClassIds = getLandingTeamClassIds(config, cached) || transient?.landingTeamClassIds;
  const initialLandingPageKey =
    config.initialLandingPageKey || cached.initialLandingPageKey || transient?.initialLandingPageKey;

  return {
    ...(landingTeamClassIds?.length ? { landingTeamClassIds: landingTeamClassIds.join(',') } : {}),
    ...(initialLandingPageKey ? { initialLandingPageKey } : {}),
  };
};

/** Reads the sign-up cache for a user, tolerating a missing or malformed entry. */
export const readOnbCache = (uuid) => {
  if (!uuid) return {};
  try {
    return JSON.parse(localStorage.getItem(getOnbCacheKey(uuid))) || {};
  } catch {
    return {};
  }
};

/** True when the sign-up cache still targets a team — landing-only leftovers don't count. */
export const hasLinkedTeams = (cached) =>
  Boolean(cached?.teamId || cached?.suggestedGroups?.length || cached?.teamKey || cached?.teamClass);

/**
 * Drops the team targeting from the sign-up cache but keeps the landing identifiers, which every
 * analytic event still reads (see `getLandingAnalytics`). Removes the entry when nothing is left.
 */
export const clearOnbCacheTeams = (uuid) => {
  const { landingTeamClassIds, initialLandingPageKey } = readOnbCache(uuid);
  const landing = {
    ...(landingTeamClassIds?.length ? { landingTeamClassIds } : {}),
    ...(initialLandingPageKey ? { initialLandingPageKey } : {}),
  };

  if (Object.keys(landing).length) {
    localStorage.setItem(getOnbCacheKey(uuid), JSON.stringify(landing));
  } else {
    localStorage.removeItem(getOnbCacheKey(uuid));
  }
};

/**
 * Drops the sign-up cache outright, landing identifiers included. Taking the landing off-ramp on
 * GroupSearchQualifyPage is the user saying the landing page was the wrong one, so nothing from it
 * should survive — not even the attribution `clearOnbCacheTeams` deliberately keeps.
 */
export const clearOnbCache = (uuid) => {
  if (!uuid) return;
  localStorage.removeItem(getOnbCacheKey(uuid));
};

export const removeSuggestionFromStorage = (uuid, teamId, teamKey) => {
  const stored = localStorage.getItem(getOnbCacheKey(uuid))
    ? JSON.parse(localStorage.getItem(getOnbCacheKey(uuid)))
    : {};

  if (stored.suggestedGroups?.length || stored.teamKey) {
    const teams = stored.suggestedGroups.filter((id, i) => stored.suggestedGroups.indexOf(id) === i && id !== teamId);
    const matchKey = teamKey && teamKey === stored.teamKey;
    const updated = {
      suggestedGroups: teams,
      isReferral: stored.isReferral,
      // Carried through so a referral still reads as one (and keeps the inviter) after the user
      // removes a suggestion — this rebuilds the stored object from scratch, so anything not
      // copied here is silently dropped.
      referer: stored.referer,
      // Same reason: without these, dismissing a suggestion would drop landing attribution for the
      // rest of the flow.
      landingTeamClassIds: stored.landingTeamClassIds,
      initialLandingPageKey: stored.initialLandingPageKey,
    };
    if (!matchKey && stored.teamKey) {
      updated.teamKey = stored.teamKey;
    }
    if (teams.length || updated.teamKey) {
      localStorage.setItem(getOnbCacheKey(uuid), JSON.stringify(updated));
    } else {
      localStorage.removeItem(getOnbCacheKey(uuid));
    }
  }
};

export const resetHeader = (pending) => {
  // Sometimes, because affiliations are slow, the page has better data than the header.
  document.body.classList.remove('never-affiliated');
  if (pending) {
    document.body.classList.add('pending');
  }
};

const EXPIRED_APPLICATION_WINDOW_DAYS = 30;

// An EXPIRED application is only "recent" if it landed within the last 30 days —
// mirrors the visibility window filterAffiliations applies to DENIED/EXPIRED applications.
export const isRecentExpiredApplication = (aff) => {
  if (aff.latestApplicationStatus !== 'EXPIRED' || !aff.latestApplication?.modifiedTime) return false;
  const daysSinceModified = Math.ceil(
    (new Date() - new Date(aff.latestApplication.modifiedTime)) / (1000 * 60 * 60 * 24),
  );
  return daysSinceModified < EXPIRED_APPLICATION_WINDOW_DAYS;
};

export const affiliationSort = (a, b) => {
  const statusPriority = {
    DRAFT: 1,
    IN_PROCESS: 2,
    APPLICANT_RESPONDED: 3,
    NEW: 4,
    EXPIRED: 5,
    DENIED: 6,
  };
  // Sort by pending status first
  if (a.pending && !b.pending) return -1;
  if (!a.pending && b.pending) return 1;

  // If both are pending, sort by latestApplicationStatus
  if (a.pending && b.pending) {
    return statusPriority[a.latestApplicationStatus] - statusPriority[b.latestApplicationStatus];
  }

  // For not pending, sort by latestApplicationStatus of DRAFT
  if (!a.pending && a.latestApplicationStatus === 'DRAFT') return -1;
  if (!b.pending && b.latestApplicationStatus === 'DRAFT') return 1;

  // Expired affiliations sort to the very end — unless they also have a recently (<30 day)
  // EXPIRED application, in which case they stay grouped with other expired applications below.
  const aExpiredAffiliation = a.expired && !isRecentExpiredApplication(a);
  const bExpiredAffiliation = b.expired && !isRecentExpiredApplication(b);
  if (aExpiredAffiliation && !bExpiredAffiliation) return 1;
  if (!aExpiredAffiliation && bExpiredAffiliation) return -1;
  if (aExpiredAffiliation && bExpiredAffiliation) return 0;

  // For not pending, sort by latestApplicationStatus of EXPIRED
  if (!a.pending && a.latestApplicationStatus === 'EXPIRED') return -1;
  if (!b.pending && b.latestApplicationStatus === 'EXPIRED') return 1;

  // For pending, sort by latestApplicationStatus of DENIED
  if (a.pending && a.latestApplicationStatus === 'DENIED') return -1;
  if (b.pending && b.latestApplicationStatus === 'DENIED') return 1;

  // Sort by expiringSoon
  if (a.expiringSoon && !b.expiringSoon) return -1;
  if (!a.expiringSoon && b.expiringSoon) return 1;

  // Sort by active
  if (a.active && !b.active) return -1;
  if (!a.active && b.active) return 1;

  return 0;
};

// Whether a team can actually be *renewed* into. A publicApplicationFormId is a brand new
// application, not a renewal, so it must not count — an expired affiliation on a team with only
// a public form has no renewal to offer and would otherwise surface a dead-end "Reapply" card.
// RETAIL teams renew via a location change rather than a form, so they qualify without one.
// Mirrors the `shouldRenew` rule in GroupCodeFlow.getFallbackApplication.
export const hasRenewalPath = (team) =>
  !!(team?.applicationsEnabled && (team?.renewalApplicationFormId || team?.type === TeamType.RETAIL));

// Whether there's a real membership to fall back on once the application that drove this row
// ages out of the 30-day window. A denied application usually leaves no affiliation at all
// (`affiliation: null`), so the empty check is the case that actually fires in production; the
// NO_STATUS and DENIED statuses are the rarer spellings of the same thing — a record of the
// *absence* of a membership rather than one. None of the three is a fallback: counting them as
// one kept denied applications on the list forever, and kept recent ones off it.
const hasAffiliationFallback = (aff) =>
  !!aff.affiliationStatus &&
  aff.affiliationStatus !== AffiliationStatus.NO_STATUS &&
  aff.affiliationStatus !== AffiliationStatus.DENIED;

export const filterAffiliations = (aff) => {
  // A deactivated org's team never shows, regardless of affiliation/application state.
  const orgStatus = aff.team?.teamOrganizationDTO?.orgStatus;
  if (orgStatus !== undefined && orgStatus !== 1) return false;

  if (
    !hasAffiliationFallback(aff) &&
    (aff.latestApplicationStatus === ApplicationStatus.DENIED ||
      aff.latestApplicationStatus === ApplicationStatus.EXPIRED)
  ) {
    // An expired application on a team with no renewal path has no way forward — hide it
    // immediately regardless of how recently it expired. Same predicate as the expired-affiliation
    // check below, deliberately: two spellings of "can renew" is what let the public-form case
    // slip through before.
    if (aff.latestApplicationStatus === ApplicationStatus.EXPIRED && !hasRenewalPath(aff.team)) {
      return false;
    }
    // TeamApplication's real field is modifiedTime, not modifiedDate (TPF-XXXXX) —
    // the modifiedDate check always returned false, so rejected/expired applications
    // with no affiliation never showed regardless of age.
    if (!aff.latestApplication?.modifiedTime) return false;
    const modifiedDate = new Date(aff.latestApplication.modifiedTime);
    const dateDifference = Math.ceil((new Date() - modifiedDate) / (1000 * 60 * 60 * 24));
    return dateDifference < 30;
  }
  // An EXPIRED affiliation with no path to renew (e.g. a private team with no renewal form)
  // has nothing actionable to show — hide it entirely rather than surface a dead-end "Reapply"
  // CTA. A pending application on top of it still earns a card.
  if (aff.affiliationStatus === AffiliationStatus.EXPIRED && !aff.pending && !hasRenewalPath(aff.team)) {
    return false;
  }
  // No real membership (NO_STATUS, DENIED, or no affiliation at all) is never shown on its
  // own — only a pending application on top of it earns a card.
  return aff.pending || hasAffiliationFallback(aff);
};

const FAST_PASS_PREFIX = 'Fast Pass:';
// This is the same logic user-affiliations uses to process.
export const isFastPassCode = ({ applicationForm }) => applicationForm?.name?.startsWith?.(FAST_PASS_PREFIX);

export const removeNonAlphanumeric = (str) => str?.replace(/[^a-zA-Z0-9]/g, '') || str;

// TODO: clean this all up after vets God willing
export const isVetCode = (code) =>
  typeof code === 'string' && ['0853877BDAEDF443', 'F2B2B47983E11935'].includes(removeNonAlphanumeric(code));

export const isElementInViewport = (el) => {
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};
